import axios, { type AxiosResponse } from "axios";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";
import { isEmpty, isString } from "lodash";
import buildQuery from "odata-query";
import qs from "qs";
import { type DefaultConfigApi, type GroupInfo, type NotifyStepSettings } from "../../../interfaces";
import { type WithPagedDataV2 } from "../../../interfaces/getPagedDataRequest";
import {
  type Filters,
  formatFiltersV2api,
  mapToV2Filters,
  serializeArrayAndFilterNullable,
} from "../../../utils/queryUtils";
import { escapeTerm } from "../../../utils/searchUtils";
import KnownHttpHeaders from "../../Application/services/knownHttpHeaders";
import { type PerformanceFilter } from "../../Library/Common/utils/performanceUtils";
import { type PaginationParams } from "../../Library/Common/models";
import { type PeopleAvailableFlow } from "../ContentAssignments/models";
import {
  type GetGroupEnrollmentUsersPreviewRequest,
  type GetGroupsConfigApi,
  type GetGroupsOptions,
  type GetGroupsUsersToAddApi,
  type GetGroupsV2Options,
  type GetOwnersResponse,
  type GetUsersParamsApiV2,
  type GroupAvailableUsers,
  v2FilterMap,
} from "../types";
import { type IGroupsDataService } from "./IGroupsDataService";

let getGroupsAbortController = new AbortController();

export const SUMMARY_CARDS_ENDPOINT = `/api/reports/v1/groups/summary/cards`;
export const USER_ACTIVITY_LINE_CHART_ENDPOINT = `/api/reports/v1/groups/activity/linechart`;
export const USER_ACTIVITY_ENGAGEMENT_ENDPOINT = `/api/reports/v1/groups/activity/engagement`;
export const USER_ENROLLMENT_LINE_CHART_ENDPOINT = `/api/reports/v1/groups/enrollment/linechart`;
export const USER_ENROLLMENT_CARDS_ENDPOINT = `/api/reports/v1/groups/enrollment/cards`;
export const FLOW_ENGAGEMENT_CHART_ENDPOINT = `/api/reports/v1/groups/engagement/flows`;
export const EMAIL_ENGAGEMENT_CHART_ENDPOINT = `/api/reports/v1/groups/engagement/emails`;
export const EMAIL_ENGAGEMENT_CARDS_ENDPOINT = `/api/reports/v1/groups/engagement/cards`;
export const PRIORITIZED_FLOW_CARDS_ENDPOINT = `/api/reports/v2/groups/prioritized/cards`;
export const PRIORITIZED_FLOW_LINE_CHART_ENDPOINT = `/api/reports/v2/groups/prioritized/linechart`;
export const PRIORITIZED_FLOW_TABLE_ENDPOINT = `/api/reports/v2/groups/prioritized/table`;
export const FLOW_USERS_EXPORT_ENDPOINT = `/api/dataexport/v1/groups/flow-users`;

export const groupsDataService: IGroupsDataService = {
  async getGroups(options: GetGroupsOptions) {
    const { skip, top, orderBy, filter, owners, fields } = options;

    const config: GetGroupsConfigApi = {
      params: {
        skip,
        top,
        filter: "IsTemplate eq false",
      },
    };

    if (orderBy) {
      config.params.orderBy = orderBy;
    }

    if (filter) {
      config.params.filter = `${config.params.filter} and ${filter}`;
    }

    if (owners) {
      config.params.owners = owners;
    }

    if (fields) {
      config.params.fields = fields;
    }

    const { data, headers } = await axios.get<GroupInfo[]>("/api/people/groups", config);

    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  async getGroupsV2(params: GetGroupsV2Options) {
    getGroupsAbortController.abort();
    getGroupsAbortController = new AbortController();
    const { data, headers } = await axios.get<GroupInfo[]>("/api/v2/groups", {
      params,
      signal: getGroupsAbortController.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });

    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },
  getGroupAvailableFlowsV4<T>(
    groupIds: number[],
    pagingOptions: WithPagedDataV2<T>,
  ): Promise<AxiosResponse<PeopleAvailableFlow[]>> {
    return axios.get(`/api/v4/groups/available-flows`, {
      params: {
        ...pagingOptions,
        id: groupIds,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  async getGroupMembersV2(
    groupId: number,
    skip: number,
    top: number,
    orderByParams?: string,
    filter?: string | Filters,
    search?: string,
  ) {
    const isV2Service = !!search;
    if (isV2Service) {
      const [sortBy, sortOrder] = orderByParams?.split(" ") || [undefined, undefined];
      const v2config: GetUsersParamsApiV2 = {
        params: {
          skip,
          top,
          term: escapeTerm(search),
          sortBy,
          sortOrder,
          ...formatFiltersV2api(mapToV2Filters(filter as Filters, v2FilterMap)),
        },
      };

      return axios.get(`/api/v2/groups/${groupId}/members`, {
        params: { ...v2config.params },
        paramsSerializer: serializeArrayAndFilterNullable,
      });
    }
    const config: DefaultConfigApi = {
      params: {
        skip,
        top,
      },
      paramsSerializer: qs.stringify,
    };

    if (orderByParams) {
      config.params.orderBy = orderByParams;
    }

    if (!isEmpty(filter) && isString(filter)) {
      config.params.filter = filter;
    } else {
      config.params = { ...config.params, ...(filter as Filters) };
    }

    return axios.get(`/api/people/groups/${groupId}/members`, config);
  },

  getGroupInfo(groupId: number) {
    return axios.get(`/api/people/groups/${groupId}/info`);
  },

  async getGroupUsersToAdd(groupIds: number[], skip: number, top: number, orderByParams?: string, filter?: any) {
    const config: GetGroupsUsersToAddApi = {
      params: {
        groupId: groupIds,
        skip,
        top,
      },
      paramsSerializer: qs.stringify,
    };

    if (orderByParams) {
      config.params.orderBy = orderByParams;
    }

    if (!isEmpty(filter) && isString(filter)) {
      config.params.filter = filter;
    } else {
      config.params = { ...config.params, ...filter };
    }

    return axios.get<GroupAvailableUsers>(`/api/groups/available-users`, {
      ...config,
      paramsSerializer: (params) => qs.stringify(params),
    });
  },

  async getGroupUsersToAddV2(
    groupIds: number[],
    skip: number,
    top: number,
    orderByParams?: string,
    filter?: any,
    search?: string,
  ) {
    const [sortBy, sortOrder] = orderByParams?.split(" ") || [undefined, undefined];
    return axios.get(`/api/v2/groups/available-users`, {
      params: {
        skip,
        top,
        term: escapeTerm(search),
        sortBy,
        sortOrder,
        ...formatFiltersV2api(mapToV2Filters(filter as Filters, v2FilterMap)),
        groupId: groupIds,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async updateGroupUsers(groupId: number, userIds: number[]) {
    return axios.post(`/api/groups/${groupId}/members`, {
      userIds,
    });
  },

  async addUsersToGroups(groupIds: number[], userIds: number[], notificationSettings?: NotifyStepSettings) {
    return axios.post("/api/v2/users/groups", {
      groupIds,
      userIds,
      notificationSettings,
    });
  },

  async removeUserFromGroups(userId: number, groupIds: number[]) {
    const config = {
      params: {
        id: groupIds,
      },
    };
    return axios.delete(`/api/users/${userId}/groups`, config);
  },

  updateGroupEnrollmentRules(groupId: number, enrollmentRules: any[], templateUsersOnly?: boolean | null) {
    return axios.put(`/api/groups/${groupId}/rules`, {
      rules: enrollmentRules,
      templateUsersOnly,
    });
  },

  async createGroup(groupPayload: any) {
    return axios.post("/api/v2/groups", groupPayload);
  },

  async deleteGroups(groupIds: number[]) {
    return axios.delete("/api/groups/delete", {
      data: { groupIds: [...groupIds] },
    });
  },

  async duplicateGroups(groupIds: number[]) {
    return axios.post(`/api/groups/duplicate`, {
      groupIds,
    });
  },

  async updateGroupInfo(groupId: number, data: any) {
    return axios.put(`/api/groups/${groupId}/info`, data);
  },

  async removeEnrolledGroupMembers(groupId: number, userIds: number[]) {
    return axios.delete(`/api/groups/${groupId}/members`, {
      data: { userIds },
    });
  },

  getOwners(groupId: number, queryParams?: object): Promise<AxiosResponse<GetOwnersResponse>> {
    const query = buildQuery(queryParams);
    return axios.get<GetOwnersResponse>(`/api/groups/${groupId}/owners${query}`);
  },

  getInheritedOwners(groupId: number, queryParams?: object): Promise<AxiosResponse<GetOwnersResponse>> {
    const query = buildQuery(queryParams);
    return axios.get<GetOwnersResponse>(`/api/groups/inherited/${groupId}/owners${query}`);
  },

  async isGroupNameExists(name: string, groupId?: number) {
    const filter = groupId ? `name eq "${name}" and id ne ${groupId}` : `name eq "${name}"`;

    const response = await axios.get("/api/people/groups", {
      params: { filter },
    });
    return response.data && !!response.data.length;
  },

  async getGroupEnrollmentUsersPreview(request: GetGroupEnrollmentUsersPreviewRequest) {
    /* istanbul ignore next */
    return axios.post(`/api/groups/rules/evaluate`, request);
  },

  async getGroupPerformanceSummaryCards(filter: PerformanceFilter, groupId: number) {
    return axios.get(SUMMARY_CARDS_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceUserActivityLineChart(filter: PerformanceFilter, groupId: number, aggregation: ChartPeriod) {
    return axios.get(USER_ACTIVITY_LINE_CHART_ENDPOINT, {
      params: {
        groupId,
        aggregation,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceUserActivityEngagement(filter: PerformanceFilter, groupId: number) {
    return axios.get(USER_ACTIVITY_ENGAGEMENT_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceUserEnrollmentLineChart(
    filter: PerformanceFilter,
    groupId: number,
    aggregation: ChartPeriod,
  ) {
    return axios.get(USER_ENROLLMENT_LINE_CHART_ENDPOINT, {
      params: {
        groupId,
        aggregation,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceUserEnrollmentCards(filter: PerformanceFilter, groupId: number) {
    return axios.get(USER_ENROLLMENT_CARDS_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceFlowEngagementChart(filter: PerformanceFilter, groupId: number) {
    return axios.get(FLOW_ENGAGEMENT_CHART_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceEmailEngagementChart(filter: PerformanceFilter, groupId: number) {
    return axios.get(EMAIL_ENGAGEMENT_CHART_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformanceEmailEngagementCards(filter: PerformanceFilter, groupId: number) {
    return axios.get(EMAIL_ENGAGEMENT_CARDS_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformancePrioritizedFlowCards(filter: PerformanceFilter, groupId: number) {
    return axios.get(PRIORITIZED_FLOW_CARDS_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformancePrioritizedFlowLineChart(
    filter: PerformanceFilter,
    groupId: number,
    aggregation: ChartPeriod,
  ) {
    return axios.get(PRIORITIZED_FLOW_LINE_CHART_ENDPOINT, {
      params: {
        groupId,
        aggregation,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  async getGroupPerformancePrioritizedFlowTable(
    filter: PerformanceFilter,
    groupId: number,
    pagination: PaginationParams,
    signal?: AbortSignal,
  ) {
    return axios.get(PRIORITIZED_FLOW_TABLE_ENDPOINT, {
      params: {
        groupId,
        ...filter,
        ...pagination,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
      signal,
    });
  },

  async getFlowUsersExport(filter: PerformanceFilter, groupId: number) {
    return axios.get(FLOW_USERS_EXPORT_ENDPOINT, {
      params: {
        groupId,
        ...filter,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
      responseType: "blob",
    });
  },
};

export default groupsDataService;
